import { Icon } from '@pypestream/design-system';
import { FC } from 'react';
import { useNavigate } from 'react-router';

import {
  findAssignedRuleSetId,
  findBoundConnectionIds,
  useChannelTemplateData,
} from '../components/channel-settings-form/channel-template-bindings';
import { Channel } from '../utils';
import { useProStudioCtxSelector } from '../xstate/app.xstate';

/**
 * What a Pype channel is bound to, set once in Project settings: shown here,
 * not edited (prototype's "Connected in project settings", `.ch-bound`).
 * Each row links to the settings page that owns it. Read through the same
 * loads as Channel Settings (`useChannelTemplateData`), so the two can never
 * disagree about a channel's bindings.
 */
export const ChannelBoundConnections: FC<{ channel: Channel }> = ({
  channel,
}) => {
  const navigate = useNavigate();
  const projectId = useProStudioCtxSelector((ctx) => ctx.project?.id);
  const { pypeId, env } = channel;
  const {
    pypeRuleGroups,
    pypeConnections,
    credentials,
    ruleGroupsLoaded,
    ruleGroupsFailed,
    liveAgentLoaded,
    liveAgentFailed,
  } = useChannelTemplateData({ enabled: true, pypeId, env });

  const connectionIds = new Set(
    findBoundConnectionIds(pypeConnections, pypeId, env)
  );
  const platforms = (pypeConnections ?? [])
    .filter((c) => connectionIds.has(c.id))
    .map((c) => c.name);
  const ruleSetId = findAssignedRuleSetId(pypeRuleGroups, pypeId, env);
  const ruleSet = (pypeRuleGroups ?? []).find((r) => r.id === ruleSetId);

  const value = ({
    loaded,
    failed,
    names,
    none,
  }: {
    loaded: boolean;
    failed: boolean;
    names: string[];
    none: string;
  }) =>
    failed
      ? 'Couldn’t load'
      : !loaded
        ? 'Loading…'
        : names.length
          ? names.join(', ')
          : none;

  const rows = [
    {
      label: 'Live agent platform',
      value: value({
        loaded: liveAgentLoaded,
        failed: liveAgentFailed,
        names: platforms,
        none: 'None',
      }),
      link: 'Manage platforms',
      path: 'live-agent-platforms',
    },
    {
      label: 'Reporting API',
      // Loaded per channel; an empty list while it loads reads as none.
      value: credentials.length
        ? credentials.map((c) => c.name).join(', ')
        : 'None',
      link: 'Manage credentials',
      path: 'reporting-api-credentials',
    },
    {
      label: 'Auto-masking rule set',
      value: value({
        loaded: ruleGroupsLoaded,
        failed: ruleGroupsFailed,
        names: ruleSet ? [ruleSet.name] : [],
        none: 'Default',
      }),
      link: 'Manage rules',
      path: 'data-privacy',
    },
  ];

  return (
    <dl
      className="c-design-channel-detail__bound"
      data-testid="bobcat-design-channel-bound"
    >
      {rows.map((r) => (
        <div key={r.label} className="c-design-channel-detail__bound-row">
          <dt>{r.label}</dt>
          <dd>
            <span>{r.value}</span>
            {projectId && (
              <button
                type="button"
                className="c-design-channel-detail__bound-link"
                onClick={() => navigate(`/${projectId}/settings/${r.path}`)}
              >
                {r.link}
                <Icon name="arrow-right" size="xsmall" />
              </button>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};
